import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';
import { api, apiFetch } from '../lib/api';
import { usePeople } from '../context/PeopleContext';
import { Card, Btn, SLabel } from '../components/ui';
import EmailSearch from '../components/EmailSearch';
import { useConfirm } from '../components/ConfirmModal';

const TIMEZONES = [
  'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Phoenix','America/Anchorage','Pacific/Honolulu','Europe/London',
  'Europe/Paris','Europe/Berlin','Asia/Tokyo','Asia/Sydney',
];

const COLOR_SWATCHES = [
  '#3B82F6','#bc8cff','#ff7b72','#3fb950','#e3b341','#ffa657',
  '#06B6D4','#f472b6','#a78bfa','#34d399','#fb923c','#60a5fa',
  '#e6edf3','#94a3b8','#f87171','#4ade80','#facc15','#38bdf8',
];

const EMOJI_CATEGORIES = {
  'Music': ['🎸','🎤','🥁','🎹','🎺','🎻','🎵','🎶','🎼','🎙','🎚','🎛','📻','🎧'],
  'Stars': ['🌟','⭐','✨','💫','🌠','🌈','⚡','🔥','💎','👑','🏆','🎖','🥇','🎗'],
  'People': ['👤','👥','🧑','👨','👩','🧑‍🎤','👨‍🎤','👩‍🎤','🧑‍💼','👨‍💼','👩‍💼','🕺','💃','🧑‍🎸'],
  'Nature': ['🌺','🌸','🌼','🌻','🍀','🌿','🌊','🏔','🌙','☀️','🌤','⛅'],
  'Objects': ['📋','📱','💻','🎬','🎥','📸','🗓','✉️','📦','🔑','💡','🎯'],
  'Symbols': ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','♾','☯','🔮','🎭'],
};


function GoogleCalendarSection({ artistId }) {
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [calendars, setCalendars] = React.useState([]);
  const [selected,  setSelected]  = React.useState([]);
  const [loading,   setLoading]   = React.useState(true);
  const [saving,    setSaving]    = React.useState(false);
  const [connected, setConnected] = React.useState(false);
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

  React.useEffect(() => {
    if (!artistId) return;
    apiFetch(`/calendar/status/${artistId}`)
      .then(d => {
        setConnected(d.connected);
        setSelected(d.calendarIds || []);
        if (d.connected) {
          return apiFetch(`/calendar/list/${artistId}`)
            .then(d2 => setCalendars(d2.calendars || []))
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [artistId]);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const [saved, setSaved] = React.useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/calendar/select/${artistId}`, { method: "PATCH", body: JSON.stringify({ calendarIds: selected }) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
    catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!await confirmAction("Disconnect Google Calendar?", { title: 'Disconnect Calendar', confirmLabel: 'Disconnect', confirmColor: 'var(--blue)' })) return;
    await apiFetch(`/calendar/disconnect/${artistId}`, { method: "DELETE" }).catch(() => {});
    setConnected(false); setCalendars([]); setSelected([]);
  };

  const connect = async () => {
    try {
      const d = await apiFetch(`/calendar/connect/${artistId}`);
      if (d.url) window.location.href = d.url;
    } catch(e) { alert(e.message); }
  };

  if (!connected) return (
    <button onClick={connect}
      style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(79,156,249,0.1)", border:"1px solid rgba(79,156,249,0.3)", borderRadius:10, padding:"10px 14px", color:"var(--blue)", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
      <i className="fa-brands fa-google" style={{ marginRight:4, fontSize:11 }}></i> Connect Google Calendar
    </button>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {loading ? <p style={{ fontSize:13, color:"var(--text3)" }}>Loading calendars...</p> : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {calendars.map(c => (
            <div key={c.id} onClick={() => toggle(c.id)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:10, border:`1px solid ${selected.includes(c.id)?"var(--blue)":"var(--border)"}`, background:selected.includes(c.id)?"rgba(79,156,249,0.08)":"var(--bg)", cursor:"pointer" }}>
              <div style={{ width:12, height:12, borderRadius:"50%", background:c.color||"var(--blue)", flexShrink:0 }} />
              <span style={{ flex:1, fontSize:14, color:"var(--text)", fontWeight:selected.includes(c.id)?700:400 }}>{c.primary ? 'Primary Calendar' : c.name}</span>
              <span style={{ fontSize:16, color:selected.includes(c.id)?"var(--blue)":"var(--border)" }}>{selected.includes(c.id)?"✓":"○"}</span>
            </div>
          ))}
          {calendars.length === 0 && <p style={{ fontSize:13, color:"var(--text3)" }}>No calendars found</p>}
        </div>
      )}
      <div style={{ display:"flex", gap:8 }}>
        <button onClick={save} disabled={saving} style={{ background: saved ? "#22c55e" : "var(--blue)", color:"white", border:"none", borderRadius:8, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"background 0.2s" }}>
          {saving ? "Saving..." : saved ? "✓ Saved!" : "Save Selection"}
        </button>
        <button onClick={disconnect} style={{ background:"rgba(248,81,73,0.15)", color:"var(--red)", border:"1px solid rgba(248,81,73,0.3)", borderRadius:8, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
          Disconnect
        </button>
      </div>
      {ConfirmDialog}
    </div>
  );
}

function LocationSearch({ value, onChange }) {
  const [query,   setQuery]   = useState(value || '');
  const [results, setResults] = useState([]);
  const [show,    setShow]    = useState(false);
  const debounceRef = useRef(null);

  const fetchPlaces = async (text) => {
    if (text.length < 2) { setResults([]); return; }
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const token   = localStorage.getItem('ds_token');
      const res     = await fetch(`${API_URL}/places/autocomplete?input=${encodeURIComponent(text)}`, {
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await res.json();
      setResults(data.predictions || []);
    } catch { setResults([]); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val); onChange(val); setShow(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPlaces(val), 300);
  };

  const select = (desc) => { setQuery(desc); onChange(desc); setShow(false); setResults([]); };

  return (
    <div style={{ position:'relative', zIndex:50 }}>
      <input value={query} onChange={handleChange} onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 200)}
        placeholder="Search city or address..."
        style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
      {show && results.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, marginTop:4, zIndex:100, overflow:'hidden' }}>
          {results.map((r, i) => (
            <div key={i} onMouseDown={() => select(r.description)}
              style={{ padding:'10px 14px', cursor:'pointer', fontSize:14, color:'var(--text)', borderBottom:'1px solid var(--border)' }}>
              <i className="fa-solid fa-location-dot" style={{ marginRight:4 }}></i> {r.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmojiPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top:0, left:0 });
  const [tab,  setTab]  = useState('Music');
  const [search, setSearch] = useState('');
  const btnRef = useRef(null);
  const ref = useRef(null);
  const allEmojis = Object.values(EMOJI_CATEGORIES).flat();
  const displayed = search ? allEmojis.filter(e => e.includes(search)) : (EMOJI_CATEGORIES[tab] || []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const dropH = 360;
      let top = rect.bottom + 6;
      top = Math.max(10, Math.min(top, window.innerHeight - dropH - 10));
      setPos({ top, left: Math.min(rect.left, window.innerWidth - 340) });
    }
    setOpen(v => !v);
  };

  return (
    <div style={{ position:'relative' }}>
      <button ref={btnRef} onClick={handleOpen}
        style={{ fontSize:32, padding:'8px 16px', borderRadius:12, border:`1px solid ${open?'var(--blue)':'var(--border)'}`, background:'var(--bg)', cursor:'pointer', transition:'border-color 0.15s' }}>
        {value || '🎵'}
      </button>
      {open && (
        <div ref={ref} style={{ position:'fixed', top:pos.top, left:pos.left, zIndex:9999, background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:16, width:320, boxShadow:'0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)', backdropFilter:'blur(20px)' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search emoji..."
            style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box', marginBottom:10 }} />
          {!search && (
            <div style={{ display:'flex', gap:4, marginBottom:10, flexWrap:'wrap' }}>
              {Object.keys(EMOJI_CATEGORIES).map(cat => (
                <button key={cat} onClick={() => setTab(cat)}
                  style={{ fontSize:11, padding:'3px 8px', borderRadius:8, border:`1px solid ${tab===cat?'var(--blue)':'var(--border)'}`, background:tab===cat?'rgba(79,156,249,0.15)':'var(--bg)', color:tab===cat?'var(--blue)':'var(--text3)', cursor:'pointer', fontFamily:'inherit' }}>
                  {cat}
                </button>
              ))}
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:4 }}>
            {displayed.map((e, i) => (
              <button key={i} onClick={() => { onChange(e); setOpen(false); setSearch(''); }}
                style={{ fontSize:22, padding:'6px', borderRadius:8, border:`2px solid ${value===e?'var(--blue)':'transparent'}`, background:value===e?'rgba(79,156,249,0.1)':'transparent', cursor:'pointer' }}>
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function InviteModal({ artist, onClose }) {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await apiFetch(`/artists/${artist.id}/invite`, {
        method: 'POST', body: JSON.stringify({ email }),
      });
      setSuccess(res.isNewUser
        ? `Invite sent to ${email} — they'll get an email to create their account.`
        : `${email} already has an account and can now view ${artist.name}'s sheets.`
      );
      setEmail('');
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems: window.innerWidth < 768 ? 'flex-end' : 'center', justifyContent:'center', padding: window.innerWidth < 768 ? 0 : 20 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'var(--card)', borderRadius: window.innerWidth < 768 ? '20px 20px 0 0' : 16, border:'1px solid var(--border)', borderBottom: window.innerWidth < 768 ? 'none' : undefined, width:'100%', maxWidth: window.innerWidth < 768 ? '100%' : 440, padding: window.innerWidth < 768 ? '12px 20px 20px' : 28, maxHeight:'92vh', overflowY:'auto', paddingBottom: window.innerWidth < 768 ? 'calc(88px + env(safe-area-inset-bottom))' : 28 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:2 }}>Invite to {artist.name}</h3>
            <p style={{ fontSize:13, color:'var(--text2)' }}>They'll get access to all published day sheets</p>
          </div>
          <button onClick={onClose} style={{ fontSize:22, color:'var(--text3)', background:'none', border:'none', cursor:'pointer' }}>×</button>
        </div>
        <form onSubmit={handleInvite} style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <EmailSearch value={email} onChange={setEmail} placeholder="Email address" />
          <button type="submit" disabled={loading}
            style={{ background:'var(--blue)', color:'white', border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:700, cursor:loading?'wait':'pointer', fontFamily:'inherit' }}>
            {loading ? 'Sending...' : 'Send Invite'}
          </button>
        </form>
        {error && <div style={{ marginTop:12, padding:'10px 14px', background:'rgba(248,81,73,0.12)', border:'1px solid rgba(248,81,73,0.3)', borderRadius:10, fontSize:13, color:'var(--red)' }}>{error}</div>}
        {success && <div style={{ marginTop:12, padding:'10px 14px', background:'rgba(63,185,80,0.1)', border:'1px solid rgba(63,185,80,0.3)', borderRadius:10, fontSize:13, color:'#3fb950' }}>{success}</div>}
      </div>
    </div>
  );
}

function PhotoCropModal({ src, onSave, onClose }) {
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [img, setImg] = useState(null);
  const SIZE = 240;

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      setImg(image);
      // Fit image to cover the circle
      const s = Math.max(SIZE / image.width, SIZE / image.height);
      setScale(s);
    };
    image.src = src;
  }, [src]);

  useEffect(() => {
    if (!img || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE/2, SIZE/2, SIZE/2, 0, Math.PI*2);
    ctx.clip();
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (SIZE - w)/2 + pos.x, (SIZE - h)/2 + pos.y, w, h);
    ctx.restore();
    // Draw circle border
    ctx.beginPath();
    ctx.arc(SIZE/2, SIZE/2, SIZE/2 - 1, 0, Math.PI*2);
    ctx.strokeStyle = 'rgba(79,156,249,0.4)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [img, scale, pos]);

  const handleMouseDown = (e) => {
    setDragging(true);
    setDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    setDragging(true);
    setDragStart({ x: t.clientX - pos.x, y: t.clientY - pos.y });
  };
  const handleTouchMove = (e) => {
    if (!dragging) return;
    const t = e.touches[0];
    setPos({ x: t.clientX - dragStart.x, y: t.clientY - dragStart.y });
  };

  const handleSave = () => {
    // Render final crop to a clean canvas
    const out = document.createElement('canvas');
    out.width = SIZE; out.height = SIZE;
    const ctx = out.getContext('2d');
    ctx.beginPath();
    ctx.arc(SIZE/2, SIZE/2, SIZE/2, 0, Math.PI*2);
    ctx.clip();
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (SIZE - w)/2 + pos.x, (SIZE - h)/2 + pos.y, w, h);
    onSave(out.toDataURL('image/jpeg', 0.9));
  };

  const minScale = img ? Math.max(SIZE / img.width, SIZE / img.height) : 0.1;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', padding:28, width:'100%', maxWidth:360, textAlign:'center' }}>
        <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:4 }}>Crop Photo</h3>
        <p style={{ fontSize:13, color:'var(--text3)', marginBottom:20 }}>Drag to reposition, zoom to fit</p>

        <div style={{ display:'inline-block', borderRadius:'50%', overflow:'hidden', cursor:'grab', marginBottom:20, lineHeight:0 }}
          onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleMouseUp}>
          <canvas ref={canvasRef} width={SIZE} height={SIZE} style={{ width:SIZE, height:SIZE }} />
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20, padding:'0 12px' }}>
          <i className="fa-solid fa-magnifying-glass-minus" style={{ fontSize:12, color:'var(--text3)' }}></i>
          <input type="range" min={minScale} max={minScale * 4} step={0.01} value={scale}
            onChange={e => setScale(parseFloat(e.target.value))}
            style={{ flex:1, accentColor:'var(--blue)' }} />
          <i className="fa-solid fa-magnifying-glass-plus" style={{ fontSize:12, color:'var(--text3)' }}></i>
        </div>

        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'10px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--text2)', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave}
            style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:'var(--blue)', color:'white', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ArtistModal({ artist, onSave, onClose }) {
  const isEdit = !!artist?.id;
  const [name,     setName]     = useState(artist?.name || '');
  const [city,     setCity]     = useState(artist?.city || '');
  const [timezone, setTimezone] = useState(artist?.timezone || 'America/New_York');
  const [emoji,    setEmoji]    = useState(artist?.emoji || '🎸');
  const [color,    setColor]    = useState(artist?.color || COLOR_SWATCHES[0]);
  const [bio,      setBio]      = useState(artist?.bio || '');
  const [dist,     setDist]     = useState((artist?.distributionList || []).join(', '));
  const [aliases,  setAliases]  = useState((artist?.aliases || []).join(', '));
  const [photo,    setPhoto]    = useState(artist?.photo || null);
  const [cropSrc,  setCropSrc]  = useState(null);
  const [autoDaySheet, setAutoDaySheet] = useState(artist?.autoDaySheet || false);
  const [autoDaySheetTime, setAutoDaySheetTime] = useState(artist?.autoDaySheetTime || '19:00');
  const [userId,   setUserId]   = useState(artist?.userId || '');
  const [users,    setUsers]    = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const photoRef = useRef();

  useEffect(() => {
    apiFetch('/users').then(setUsers).catch(() => setUsers([]));
  }, []);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const body = {
        name: name.trim(), city: city.trim() || null, timezone, emoji, color,
        bio: bio.trim() || null, photo: photo || null,
        distributionList: dist.split(',').map(e => e.trim()).filter(Boolean),
        aliases: aliases.split(',').map(e => e.trim()).filter(Boolean),
        autoDaySheet, autoDaySheetTime,
        userId: userId || null,
      };
      const result = isEdit
        ? await apiFetch(`/artists/${artist.id}`, { method:'PATCH', body: JSON.stringify(body) })
        : await apiFetch('/artists', { method:'POST', body: JSON.stringify(body) });
      onSave(result);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems: window.innerWidth < 768 ? 'flex-end' : 'center', justifyContent:'center', padding: window.innerWidth < 768 ? 0 : 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--card)', borderRadius: window.innerWidth < 768 ? '20px 20px 0 0' : 16, border:'1px solid var(--border)', borderBottom: window.innerWidth < 768 ? 'none' : undefined, width:'100%', maxWidth: window.innerWidth < 768 ? '100%' : 560, maxHeight:'92vh', overflowY:'auto', paddingBottom: window.innerWidth < 768 ? 'calc(88px + env(safe-area-inset-bottom))' : undefined }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px', borderBottom:'1px solid var(--border)', position:'sticky', top:0, background:'var(--card)', zIndex:10 }}>
          <h2 style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>{isEdit ? 'Edit Artist' : 'New Artist'}</h2>
          <button onClick={onClose} style={{ fontSize:22, color:'var(--text3)', background:'none', border:'none', cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:0 }}>
          {/* ── Identity ── */}
          <p style={{ fontSize:10, fontWeight:800, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', marginBottom:14 }}>Identity</p>
          <div style={{ display:'flex', gap:16, alignItems:'flex-start', marginBottom:16 }}>
            <div>
              <div style={{ position:'relative', display:'inline-block' }}>
                <div onClick={() => photoRef.current.click()}
                  style={{ width:72, height:72, borderRadius:'50%', border:`2px dashed ${photo ? 'var(--blue)' : 'var(--border)'}`, background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', overflow:'hidden', transition:'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='var(--blue)'} onMouseLeave={e => { if(!photo) e.currentTarget.style.borderColor='var(--border)'; }}>
                  {photo ? <img src={photo} alt="artist" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <i className="fa-solid fa-camera" style={{ fontSize:24, color:'var(--text3)' }}></i>}
                </div>
                {photo && (
                  <button onClick={() => setPhoto(null)}
                    style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:'var(--red)', border:'none', cursor:'pointer', color:'white', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                    ✕
                  </button>
                )}
              </div>
              <input ref={photoRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handlePhoto} />
            </div>
            <div>
              <EmojiPicker value={emoji} onChange={setEmoji} />
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {COLOR_SWATCHES.map(c => (
                  <button key={c} onClick={() => setColor(c)}
                    style={{ width:24, height:24, borderRadius:'50%', background:c, border:`2.5px solid ${color===c?'white':'transparent'}`, cursor:'pointer', padding:0, boxShadow:color===c?`0 0 0 2px ${c}`:'none', transition:'all 0.12s' }} />
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <SLabel>Name *</SLabel>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Artist or act name"
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
          </div>
          <div style={{ marginBottom:14 }}>
            <SLabel>Aliases</SLabel>
            <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder='KJ, Kevin J, Mr. Jonas...'
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
            <p style={{ fontSize:11, color:'var(--text3)', marginTop:5 }}>Comma-separated — used to match reservations and bookings</p>
          </div>
          <div style={{ marginBottom:14 }}>
            <SLabel>Bio</SLabel>
            <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Short description..." rows={2}
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
          </div>

          {/* ── Account ── */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:14 }}>
            <p style={{ fontSize:10, fontWeight:800, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', marginBottom:14 }}>Account</p>
            <SLabel>User Account for this Artist</SLabel>
            <select value={userId} onChange={e => setUserId(e.target.value)}
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}>
              <option value="">— Not linked —</option>
              {users.map(u => {
                const label = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id;
                return <option key={u.id} value={u.id}>{label} ({u.email})</option>;
              })}
            </select>
            <p style={{ fontSize:11, color:'var(--text3)', marginTop:6 }}>
              Approvals created for {name.trim() || 'this artist'} will auto-route to this user's viewer app.
            </p>
          </div>

          {/* ── Location ── */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:14 }}>
            <p style={{ fontSize:10, fontWeight:800, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', marginBottom:14 }}>Location</p>
            <div style={{ marginBottom:14 }}>
              <SLabel>Home City</SLabel>
              <LocationSearch value={city} onChange={setCity} />
            </div>
            <div>
              <SLabel>Timezone</SLabel>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}
                style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>

          {/* ── Distribution ── */}
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:14 }}>
            <p style={{ fontSize:10, fontWeight:800, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', marginBottom:14 }}>Distribution</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
              {dist.split(',').map(e=>e.trim()).filter(Boolean).map((email,i) => (
                <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4, background:'rgba(79,156,249,0.1)', border:'1px solid rgba(79,156,249,0.25)', borderRadius:20, padding:'4px 12px', fontSize:12, color:'var(--blue)', fontWeight:600 }}>
                  {email}
                  <button onClick={() => setDist(dist.split(',').map(e=>e.trim()).filter(Boolean).filter((_,j)=>j!==i).join(', '))}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:14, padding:0, lineHeight:1, marginLeft:2 }}>×</button>
                </span>
              ))}
              {dist.split(',').filter(e=>e.trim()).length === 0 && (
                <p style={{ fontSize:12, color:'var(--text3)', fontStyle:'italic' }}>No recipients added</p>
              )}
            </div>
            <EmailSearch value="" onSelect={(email) => {
              if (!email) return;
              const existing = dist.split(',').map(e=>e.trim()).filter(Boolean);
              if (!existing.includes(email)) setDist([...existing, email].join(', '));
            }} placeholder="Add email to distribution list..." />
          </div>

          {/* ── Google Calendar ── */}
          {isEdit && (
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:14 }}>
              <p style={{ fontSize:10, fontWeight:800, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', marginBottom:14 }}>Google Calendar</p>
              <GoogleCalendarSection artistId={artist?.id} />
            </div>
          )}

          {/* ── Auto Day Sheet ── */}
          {isEdit && (
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:18, marginBottom:14 }}>
              <p style={{ fontSize:10, fontWeight:800, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', marginBottom:14 }}>Auto Day Sheet</p>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: autoDaySheet ? 12 : 0 }}>
                <div>
                  <p style={{ fontSize:14, fontWeight:700, color:'var(--text)', margin:0 }}>Generate & send daily</p>
                  <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0' }}>Auto-creates tomorrow's sheet from calendar & travel</p>
                </div>
                <button onClick={() => setAutoDaySheet(!autoDaySheet)}
                  style={{ width:48, height:28, borderRadius:14, border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s',
                    background: autoDaySheet ? 'var(--green)' : 'var(--border)' }}>
                  <div style={{ width:22, height:22, borderRadius:11, background:'#fff', position:'absolute', top:3,
                    left: autoDaySheet ? 23 : 3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                </button>
              </div>
              {autoDaySheet && (
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'rgba(63,185,80,0.06)', border:'1px solid rgba(63,185,80,0.15)', borderRadius:10 }}>
                  <i className="fa-solid fa-clock" style={{ fontSize:12, color:'var(--green)' }}></i>
                  <span style={{ fontSize:13, color:'var(--text2)' }}>Send at</span>
                  <input type="time" value={autoDaySheetTime} onChange={e => setAutoDaySheetTime(e.target.value)}
                    style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 10px', fontSize:13, color:'var(--text)', fontFamily:'inherit', outline:'none' }} />
                  <span style={{ fontSize:11, color:'var(--text3)' }}>evening before</span>
                </div>
              )}
            </div>
          )}

          {error && <div style={{ padding:'10px 14px', background:'rgba(248,81,73,0.12)', border:'1px solid rgba(248,81,73,0.3)', borderRadius:10, fontSize:13, color:'var(--red)', marginBottom:14 }}>{error}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', paddingTop:8 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Artist'}
            </Btn>
          </div>
        </div>
      </div>
      {cropSrc && (
        <PhotoCropModal src={cropSrc} onSave={(cropped) => { setPhoto(cropped); setCropSrc(null); }} onClose={() => setCropSrc(null)} />
      )}
    </div>
  );
}

const TEAM_PALETTE = ['#4da6ff','#a78bfa','#f472b6','#34d399','#fbbf24','#f87171','#fb923c','#06B6D4','#818cf8','#e879f9'];
const ROLES = ['artist','team','crew','family','other'];
const ROLE_COLORS = { artist:'#1E40AF', team:'#fbbf24', manager:'#fbbf24', crew:'#34d399', family:'#f472b6', other:'#94a3b8' };
const ACCESS_LEVELS = ['admin','viewer'];

function PersonModal({ person, artistId, artists, onSave, onClose }) {
  const cap = (s) => s ? s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';
  // Determine the team tag — ignore access roles like admin/viewer
  const personTag = person?.tags?.find(t => ROLES.includes(t)) || (ROLES.includes(person?.role) ? person.role : null);
  const [name, setName] = useState(cap(person?.name || ''));
  const [fullName, setFullName] = useState(cap(person?.fullName || ''));
  const [role, setRole] = useState(personTag || 'crew');
  const [customRole, setCustomRole] = useState(!personTag && person?.role && !['admin','viewer'].includes(person?.role) ? person.role : '');
  const [email, setEmail] = useState(person?.email || '');
  const [color, setColor] = useState(person?.color || TEAM_PALETTE[Math.floor(Math.random()*TEAM_PALETTE.length)]);
  const [access, setAccess] = useState(person?.access && person.access !== 'none' ? person.access : 'viewer');
  const [linkedArtistIds, setLinkedArtistIds] = useState(person?.linkedArtistIds || (artistId ? [artistId] : []));
  const [superViewerArtistIds, setSuperViewerArtistIds] = useState(person?.superViewerArtistIds || []);
  const [canApprove, setCanApprove] = useState(!!person?.canApprove);
  const [canCreateApproval, setCanCreateApproval] = useState(!!person?.canCreateApproval);
  const [saving, setSaving] = useState(false);

  const toggleArtist = (id) => setLinkedArtistIds(prev => {
    const next = prev.includes(id) ? prev.filter(a=>a!==id) : [...prev, id];
    // Unlinking kills super-viewer privilege for that artist
    if (!next.includes(id)) setSuperViewerArtistIds(s => s.filter(a => a !== id));
    return next;
  });
  const toggleSuper = (id) => setSuperViewerArtistIds(prev =>
    prev.includes(id) ? prev.filter(a=>a!==id) : [...prev, id]
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems: window.innerWidth < 768 ? 'flex-end' : 'center', justifyContent:'center', padding: window.innerWidth < 768 ? 0 : 20 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg2)', borderRadius: window.innerWidth < 768 ? '20px 20px 0 0' : 20, width:'100%', maxWidth: window.innerWidth < 768 ? '100%' : 480, padding: window.innerWidth < 768 ? '12px 20px 20px' : 28, border:'1px solid var(--border)', borderBottom: window.innerWidth < 768 ? 'none' : undefined, maxHeight:'92vh', overflowY:'auto', paddingBottom: window.innerWidth < 768 ? 'calc(88px + env(safe-area-inset-bottom))' : 28 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <h3 style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>{person ? 'Edit Person' : 'Add Person'}</h3>
          <button onClick={onClose} style={{ width:32, height:32, borderRadius:16, background:'var(--bg3)', border:'none', cursor:'pointer', fontSize:14, color:'var(--text2)' }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <SLabel>Full Name</SLabel>
              <input value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="e.g. Kevin Jonas"
                style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }} autoFocus />
            </div>
            <div>
              <SLabel>Short Name</SLabel>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. NJ, K2, Holly"
                style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
            </div>
          </div>
          <div>
            <SLabel>Email</SLabel>
            <EmailSearch value={email} onChange={setEmail} placeholder="email@example.com" />
          </div>
          <div>
            <SLabel>Tag</SLabel>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {ROLES.map(r => (
                <button key={r} onClick={() => setRole(r)}
                  style={{ padding:'6px 14px', borderRadius:20, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, textTransform:'capitalize',
                    background: role===r ? (ROLE_COLORS[r]||'#94a3b8')+'22' : 'var(--bg3)',
                    borderColor: role===r ? ROLE_COLORS[r]||'#94a3b8' : 'var(--border)',
                    color: role===r ? ROLE_COLORS[r]||'#94a3b8' : 'var(--text2)' }}>
                  {r === 'other' ? '+ Custom' : r}
                </button>
              ))}
            </div>
            {role === 'other' && (
              <input value={customRole} onChange={e=>setCustomRole(e.target.value)} placeholder="Type custom role..." autoFocus
                style={{ marginTop:8, width:'100%', background:'var(--bg3)', border:'1px solid var(--blue)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
            )}
          </div>
          <div>
            <SLabel>Access</SLabel>
            <div style={{ display:'flex', gap:6 }}>
              {ACCESS_LEVELS.map(a => (
                <button key={a} onClick={() => setAccess(a)}
                  style={{ padding:'6px 14px', borderRadius:20, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, textTransform:'capitalize',
                    background: access===a ? 'rgba(30,64,175,0.15)' : 'var(--bg3)',
                    borderColor: access===a ? 'var(--blue)' : 'var(--border)',
                    color: access===a ? 'var(--blue)' : 'var(--text2)' }}>
                  {a}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:8, padding:'8px 12px', borderRadius:10, background:'rgba(30,64,175,0.06)', border:'1px solid rgba(30,64,175,0.15)' }}>
              <i className="fa-solid fa-envelope" style={{ fontSize:11, color:'var(--blue)' }}></i>
              <p style={{ fontSize:12, color:'var(--blue)', margin:0, lineHeight:1.4 }}>
                {email ? `An invite email will be sent to ${email}` : 'Add an email above to send an invite'}
              </p>
            </div>
          </div>
          <div>
            <SLabel>Artists</SLabel>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {artists.map(a => (
                <button key={a.id} onClick={() => toggleArtist(a.id)}
                  style={{ padding:'6px 12px', borderRadius:10, cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600,
                    background: linkedArtistIds.includes(a.id) ? 'rgba(79,156,249,0.1)' : 'var(--bg3)',
                    border: '1px solid ' + (linkedArtistIds.includes(a.id) ? 'var(--blue)' : 'var(--border)'),
                    color: linkedArtistIds.includes(a.id) ? 'var(--blue)' : 'var(--text2)', display:'flex', alignItems:'center', gap:6 }}>
                  {linkedArtistIds.includes(a.id) ? '✓' : '○'} {a.name?.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <SLabel>Color</SLabel>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {TEAM_PALETTE.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  style={{ width:28, height:28, borderRadius:'50%', background:c, border: color===c ? '3px solid white' : '2px solid transparent',
                    cursor:'pointer', boxShadow: color===c ? `0 0 0 2px ${c}` : 'none', outline:'none' }} />
              ))}
            </div>
          </div>
          <div style={{ padding:12, borderRadius:12, background:'var(--bg3)', border:'1px solid var(--border)' }}>
            <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, cursor:'pointer' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>Can Create Approvals</div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>
                  Let this person post approval requests from the viewer app. Anyone — artist, family, crew, manager — can be granted this regardless of their tag.
                </div>
              </div>
              <input type="checkbox" checked={canCreateApproval} onChange={e => setCanCreateApproval(e.target.checked)}
                style={{ width:20, height:20, cursor:'pointer', accentColor:'var(--blue)' }} />
            </label>
          </div>
          <button onClick={async () => {
            if (!name.trim()) return;
            setSaving(true);
            try {
              const finalRole = role === 'other' && customRole.trim() ? customRole.trim().toLowerCase() : role;
              await onSave({ name: name.trim(), fullName: fullName.trim()||null, role: finalRole, email: email.trim()||null, color, access, linkedArtistIds, superViewerArtistIds, canApprove, canCreateApproval });
              onClose();
            } catch(e) { alert(e.message); }
            finally { setSaving(false); }
          }} disabled={saving || !name.trim()}
            style={{ width:'100%', padding:'14px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer', opacity: (saving||!name.trim()) ? 0.5 : 1 }}>
            {saving ? 'Saving...' : person ? 'Save Changes' : access !== 'none' && email ? 'Add & Send Invite' : 'Add Person'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AccessCodeSection({ artist, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(artist.accessCode || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateCode = () => {
    const prefix = artist.name.replace(/[^a-zA-Z]/g, '').slice(0, 6).toUpperCase();
    const suffix = String(Math.floor(Math.random() * 9000) + 1000);
    setCode(`${prefix}-${suffix}`);
    setEditing(true);
  };

  const saveCode = async () => {
    setSaving(true);
    try {
      const result = await apiFetch(`/artists/${artist.id}`, { method: 'PATCH', body: JSON.stringify({ accessCode: code.trim() || null }) });
      onUpdate(result);
      setEditing(false);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(artist.accessCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!artist.accessCode && !editing) {
    return (
      <div style={{ padding:'0 16px 4px', textAlign:'center' }}>
        <button onClick={generateCode}
          style={{ fontSize:12, color:'var(--blue)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:600, padding:'6px 0', display:'inline-flex', alignItems:'center', gap:5 }}>
          <i className="fa-solid fa-key" style={{ fontSize:10 }}></i> Generate Access Code
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={{ padding:'8px 16px 4px' }}>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} maxLength={20}
            style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px', fontSize:13, fontWeight:700, color:'var(--text)', fontFamily:'monospace', letterSpacing:1, outline:'none', textAlign:'center', boxSizing:'border-box' }}
            onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} autoFocus />
          <button onClick={saveCode} disabled={saving}
            style={{ padding:'8px 12px', borderRadius:8, background:'var(--blue)', border:'none', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
            {saving ? '...' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setCode(artist.accessCode || ''); }}
            style={{ padding:'8px', borderRadius:8, background:'none', border:'1px solid var(--border)', color:'var(--text3)', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding:'8px 16px 4px', textAlign:'center' }}>
      <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(30,64,175,0.06)', border:'1px solid rgba(30,64,175,0.15)', borderRadius:10, padding:'6px 12px' }}>
        <i className="fa-solid fa-key" style={{ fontSize:10, color:'var(--blue)' }}></i>
        <span style={{ fontSize:13, fontWeight:700, color:'var(--text)', fontFamily:'monospace', letterSpacing:1 }}>{artist.accessCode}</span>
        <button onClick={copyCode} title="Copy code"
          style={{ background:'none', border:'none', cursor:'pointer', color: copied ? '#22c55e' : 'var(--blue)', fontSize:12, padding:'2px 4px' }}>
          <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}></i>
        </button>
        <button onClick={() => { setCode(artist.accessCode); setEditing(true); }} title="Edit code"
          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:11, padding:'2px 4px' }}>
          <i className="fa-solid fa-pen"></i>
        </button>
      </div>
    </div>
  );
}

export default function PeoplePage({ defaultInvite, usersOnly, artistsTab }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { reload } = usePeople();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [artists,  setArtists]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [inviting, setInviting] = useState(null);
  // People state
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [personModal, setPersonModal] = useState(null);
  const [peopleTab, setPeopleTab] = useState('people');
  // Batch invite state
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchArtistIds, setBatchArtistIds] = useState([]);
  const [batchEmails, setBatchEmails] = useState('');
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const [batchSending, setBatchSending] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [batchRole, setBatchRole] = useState('viewer');
  const [batchTag, setBatchTag] = useState('crew');
  const [codeEmailArtist, setCodeEmailArtist] = useState(null); // artist id showing email input
  const [codeEmailValue, setCodeEmailValue] = useState('');
  const [codeEmailSending, setCodeEmailSending] = useState(false);
  const [codeEmailMsg, setCodeEmailMsg] = useState('');
  const [sharedArtistId, setSharedArtistId] = useState(null);

  // Auto-open invite section from prop or ?invite=1
  useEffect(() => {
    if (defaultInvite || searchParams.get('invite') === '1') {
      setBatchOpen(true);
      if (artists.length && batchArtistIds.length === 0) setBatchArtistIds([artists[0].id]);
    }
  }, [defaultInvite, searchParams, artists]);

  useEffect(() => {
    api.getArtists().then(d => {
      const list = d.artists || [];
      setArtists(list);
      // Auto-open edit modal if redirected back after Google connect
      const params = new URLSearchParams(window.location.search);
      const connectedId = params.get('connected');
      if (connectedId) {
        const a = list.find(x => x.id === connectedId);
        if (a) setModal(a);
        window.history.replaceState({}, '', '/admin/people');
      }
    }).catch(() => {}).finally(() => setLoading(false));
    // Load users
    apiFetch('/users').then(u => setUsers(Array.isArray(u) ? u : u.users || [])).catch(() => {});
  }, []);

  // Load team members for all artists
  useEffect(() => {
    if (!artists.length) return;
    Promise.all(artists.map(a => apiFetch(`/artists/${a.id}/team`).then(d => (Array.isArray(d)?d:[]).map(m => ({...m, artistId: a.id, artistName: a.name}))).catch(()=>[])
    )).then(results => setMembers(results.flat()));
  }, [artists]);

  // Merge members + users into unified people list
  const people = (() => {
    const map = new Map();
    // Team members first
    members.forEach(m => {
      const key = (m.email || m.name || m.id).toLowerCase();
      if (!map.has(key)) {
        map.set(key, { ...m, type:'member', tags:[m.role], access:'none', linkedArtists:[m.artistName], linkedArtistIds: [m.artistId] });
      } else {
        const existing = map.get(key);
        if (!existing.tags.includes(m.role)) existing.tags.push(m.role);
        if (!existing.linkedArtists.includes(m.artistName)) existing.linkedArtists.push(m.artistName);
        if (!existing.linkedArtistIds) existing.linkedArtistIds = [];
        if (!existing.linkedArtistIds.includes(m.artistId)) existing.linkedArtistIds.push(m.artistId);
      }
    });
    // Users — merge or add
    users.forEach(u => {
      const key = (u.email || u.id).toLowerCase();
      const userLinkedIds = u.linkedArtistIds || [];
      if (map.has(key)) {
        const existing = map.get(key);
        existing.access = u.role || 'viewer';
        existing.userId = u.id;
        existing.userEmail = u.email;
        existing.accountStatus = u.accountStatus || 'active';
        existing.linkedArtistIds = userLinkedIds;
        existing.superViewerArtistIds = u.superViewerArtistIds || [];
        existing.canApprove = !!u.canApprove;
        existing.canCreateApproval = !!u.canCreateApproval;
        // Merge linkedArtists from user record
        artists.filter(a => userLinkedIds.includes(a.id)).forEach(a => {
          if (!existing.linkedArtists) existing.linkedArtists = [];
          if (!existing.linkedArtists.includes(a.name)) existing.linkedArtists.push(a.name);
        });
      } else {
        const name = u.firstName ? `${u.firstName} ${u.lastName||''}`.trim() : u.email?.split('@')[0] || '?';
        map.set(key, { name, fullName: name, email: u.email, role: u.role, type:'user', tags:[], access: u.role || 'viewer', userId: u.id, accountStatus: u.accountStatus || 'active', linkedArtistIds: userLinkedIds, superViewerArtistIds: u.superViewerArtistIds || [], canApprove: !!u.canApprove, canCreateApproval: !!u.canCreateApproval, linkedArtists: artists.filter(a => userLinkedIds.includes(a.id)).map(a => a.name), color: '#94a3b8' });
      }
    });
    return [...map.values()].sort((a,b) => {
      const order = { artist:0, manager:1, crew:2, family:3, other:4 };
      return (order[a.tags?.[0]]??5) - (order[b.tags?.[0]]??5);
    });
  })();

  const handlePersonSave = async (data) => {
    // Save as team member — update existing or create new
    for (const artId of (data.linkedArtistIds || [])) {
      // Check if this person already exists as a team member for this artist
      const existing = members.find(m => m.artistId === artId && (
        (m.email && data.email && m.email.toLowerCase() === data.email.toLowerCase()) ||
        (m.name && m.name.toLowerCase() === data.name.toLowerCase())
      ));
      if (existing) {
        await apiFetch(`/artists/${artId}/team/${existing.id}`, { method: 'PATCH', body: JSON.stringify({ name: data.name, fullName: data.fullName, role: data.role, email: data.email, color: data.color }) });
      } else {
        await api.addTeamMember(artId, { name: data.name, fullName: data.fullName, role: data.role, email: data.email, color: data.color });
      }
    }
    // Update user role + linkedArtistIds if user exists
    if (data.email) {
      const existingUser = users.find(u => u.email?.toLowerCase() === data.email.toLowerCase());
      if (existingUser && data.access && data.access !== 'none') {
        try {
          await apiFetch(`/users/${existingUser.id}`, { method:'PATCH', body: JSON.stringify({ role: data.access, linkedArtistIds: data.linkedArtistIds, superViewerArtistIds: data.superViewerArtistIds || [], canApprove: !!data.canApprove, canCreateApproval: !!data.canCreateApproval }) });
        } catch(e) { /* ok */ }
      }
    }
    // If access is admin/viewer and has email, send invite
    if (data.access && data.access !== 'none' && data.email) {
      const artistId = data.linkedArtistIds?.[0] || artists[0]?.id;
      if (artistId) {
        try {
          await apiFetch(`/artists/${artistId}/invite`, { method:'POST', body: JSON.stringify({ email: data.email }) });
        } catch(e) { /* user may already exist, that's ok */ }
      }
    }
    // Reload all
    const results = await Promise.all(artists.map(a => apiFetch(`/artists/${a.id}/team`).then(d => (Array.isArray(d)?d:[]).map(m => ({...m, artistId: a.id, artistName: a.name}))).catch(()=>[])));
    setMembers(results.flat());
    apiFetch('/users').then(u => setUsers(Array.isArray(u) ? u : u.users || [])).catch(() => {});
    reload();
  };

  const handlePersonDelete = async (person) => {
    if (!await confirmAction(`Remove ${person.fullName || person.name}?`, { title: 'Remove Person', confirmLabel: 'Remove' })) return;
    try {
      if (person.type === 'user' && person.userId) {
        await apiFetch(`/users/${person.userId}`, { method: 'DELETE' });
      } else if (person.id && person.artistId) {
        await api.deleteTeamMember(person.artistId, person.id);
      }
    } catch (e) { alert('Error: ' + e.message); return; }
    const results = await Promise.all(artists.map(a => apiFetch(`/artists/${a.id}/team`).then(d => (Array.isArray(d)?d:[]).map(m => ({...m, artistId: a.id, artistName: a.name}))).catch(()=>[])));
    setMembers(results.flat());
    apiFetch('/users').then(u => setUsers(Array.isArray(u) ? u : u.users || [])).catch(() => {});
    reload();
  };

  const handleSave = (result) => {
    setArtists(prev => {
      const idx = prev.findIndex(a => a.id === result.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = result; return next; }
      return [...prev, result];
    });
    reload();
    setModal(null);
  };

  const handleDelete = async (artist) => {
    if (!await confirmAction(`Delete ${artist.name}? This cannot be undone.`, { title: 'Delete Artist', confirmLabel: 'Delete' })) return;
    setDeleting(artist.id);
    try {
      await apiFetch(`/artists/${artist.id}`, { method: 'DELETE' });
      setArtists(prev => prev.filter(a => a.id !== artist.id));
      reload();
    } catch (e) { alert(e.message); }
    finally { setDeleting(null); }
  };

  if (loading) return <div style={{ padding:40, color:'var(--text2)' }}>Loading...</div>;

  // Artists tab — shows artist cards + invite
  if (artistsTab) {
    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:900, margin:'0 auto' }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontSize:32, fontWeight:900, letterSpacing:-0.5, color:'var(--text)' }}>Artists</h1>
          <p style={{ fontSize:14, color:'var(--text2)', marginTop:4 }}>Manage artists and send invites</p>
        </div>

        {/* Artist cards */}
        {isMobile ? (
          <div style={{ marginBottom:20 }}>
            <div style={{ display:'flex', gap:16, overflowX:'auto', padding:'4px 0 12px', WebkitOverflowScrolling:'touch' }}>
              {artists.map(a => (
                <button key={a.id} onClick={() => setModal(a)}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0, minWidth:68 }}>
                  <div style={{ width:60, height:60, borderRadius:'50%', background:`${a.color}20`, border:`2.5px solid ${a.color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, overflow:'hidden' }}>
                    {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color:'var(--text)', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
                </button>
              ))}
              <button onClick={() => setModal('new')}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0, minWidth:68 }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:'var(--bg)', border:'2px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <i className="fa-solid fa-plus" style={{ fontSize:18, color:'var(--text3)' }}></i>
                </div>
                <span style={{ fontSize:11, fontWeight:600, color:'var(--text3)' }}>Add</span>
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:20, marginBottom:24 }}>
            {artists.map(a => (
              <div key={a.id} style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden', transition:'transform 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
                <div style={{ height:56, background:`linear-gradient(180deg, ${a.color}15 0%, transparent 100%)` }} />
                <div style={{ display:'flex', justifyContent:'center', marginTop:-32 }}>
                  <div style={{ width:64, height:64, borderRadius:16, background:`${a.color}15`, border:'3px solid var(--card)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, overflow:'hidden' }}>
                    {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                  </div>
                </div>
                <div style={{ textAlign:'center', padding:'10px 20px 0' }}>
                  <h2 style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:2 }}>{a.name}</h2>
                  {a.city && <p style={{ fontSize:13, color:'var(--text2)' }}><i className="fa-solid fa-location-dot" style={{ marginRight:4, fontSize:11 }}></i>{a.city}</p>}
                </div>
                <AccessCodeSection artist={a} onUpdate={(updated) => setArtists(prev => prev.map(x => x.id === updated.id ? updated : x))} />
                {/* Google Calendar */}
                <div style={{ padding:'4px 16px 0' }}>
                  {a.googleCalendarId ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'8px 12px', borderRadius:10, background:'rgba(63,185,80,0.08)', border:'1px solid rgba(63,185,80,0.2)' }}>
                      <i className="fa-brands fa-google" style={{ fontSize:11, color:'#3fb950' }}></i>
                      <span style={{ fontSize:12, fontWeight:700, color:'#3fb950' }}>Calendar Connected</span>
                    </div>
                  ) : (
                    <button onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const d = await apiFetch(`/calendar/connect/${a.id}`);
                        if (d.url) window.location.href = d.url;
                      } catch(err) { alert(err.message); }
                    }}
                      style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'10px 14px', borderRadius:10, background:'rgba(66,133,244,0.1)', border:'1.5px solid rgba(66,133,244,0.3)', color:'#4285f4', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
                      <i className="fa-brands fa-google" style={{ fontSize:12 }}></i> Connect Google Calendar
                    </button>
                  )}
                </div>
                <div style={{ display:'flex', gap:6, padding:'12px 16px 18px', justifyContent:'center' }}>
                  <Btn variant="ghost" size="sm" onClick={() => setModal(a)}><i className="fa-solid fa-pen" style={{ fontSize:11 }}></i></Btn>
                  <Btn variant="danger" size="sm" onClick={() => handleDelete(a)} disabled={deleting === a.id}>{deleting === a.id ? '...' : <i className="fa-solid fa-trash" style={{ fontSize:11 }}></i>}</Btn>
                </div>
              </div>
            ))}
            <div onClick={() => setModal('new')} style={{ background:'var(--card)', borderRadius:16, border:'2px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center', minHeight:200, cursor:'pointer', transition:'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='var(--blue)'} onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{ textAlign:'center' }}>
                <i className="fa-solid fa-plus" style={{ fontSize:32, color:'var(--text3)', display:'block', marginBottom:8 }}></i>
                <p style={{ fontSize:14, fontWeight:700, color:'var(--text3)' }}>New Artist</p>
              </div>
            </div>
          </div>
        )}

        {/* Invite section */}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:24 }}>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>Invite to Artist</h2>
          <p style={{ fontSize:14, color:'var(--text2)', marginBottom:20 }}>Send invite emails - single or batch</p>

          <Card style={{ padding:20, marginBottom:20 }}>
            <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:10, textTransform:'uppercase' }}>Select Artists <span style={{ fontWeight:400, letterSpacing:0, textTransform:'none' }}>(tap multiple)</span></label>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {artists.map(a => {
                const sel = batchArtistIds.includes(a.id);
                return (
                  <button key={a.id} onClick={() => setBatchArtistIds(prev => sel ? prev.filter(id => id !== a.id) : [...prev, a.id])}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700,
                      background: sel ? 'rgba(30,64,175,0.08)' : 'var(--bg)', border: `2px solid ${sel ? 'var(--blue)' : 'var(--border)'}`, color: sel ? 'var(--text)' : 'var(--text3)' }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', overflow:'hidden', background:`${a.color||'var(--blue)'}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                    </div>
                    {a.name}
                    {sel && <i className="fa-solid fa-check" style={{ fontSize:12, color:'var(--blue)', marginLeft:2 }}></i>}
                  </button>
                );
              })}
            </div>
          </Card>

          <Card style={{ padding:20, marginBottom:20 }}>
            <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:8, textTransform:'uppercase' }}>Email</label>
            <div style={{ display:'flex', gap:6 }}>
              <div style={{ flex:1 }}>
                <EmailSearch value={inviteEmailInput} onChange={setInviteEmailInput} onSelect={(email) => {
                  if (!email) return;
                  const existing = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
                  if (!existing.includes(email)) setBatchEmails(prev => (prev.trim() ? prev.trim() + '\n' : '') + email);
                  setInviteEmailInput('');
                }} placeholder="Search contacts or type email..." />
              </div>
              <button onClick={() => {
                const email = inviteEmailInput.trim().toLowerCase();
                if (!email.includes('@')) return;
                const existing = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
                if (!existing.includes(email)) setBatchEmails(prev => (prev.trim() ? prev.trim() + '\n' : '') + email);
                setInviteEmailInput('');
              }} disabled={!inviteEmailInput.trim().includes('@')}
                style={{ padding:'8px 14px', borderRadius:10, background:'var(--blue)', border:'none', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity: inviteEmailInput.trim().includes('@') ? 1 : 0.4, alignSelf:'flex-start' }}>
                Add
              </button>
            </div>
            {batchEmails.trim() && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:12 }}>
                {batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean).map((email, i) => (
                  <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'5px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                    background: email.includes('@') ? 'rgba(30,64,175,0.1)' : 'rgba(239,68,68,0.1)',
                    border: `1px solid ${email.includes('@') ? 'rgba(30,64,175,0.25)' : 'rgba(239,68,68,0.25)'}`,
                    color: email.includes('@') ? 'var(--blue)' : '#ef4444' }}>
                    {email}
                    <button onClick={() => { const list = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean); list.splice(i, 1); setBatchEmails(list.join('\n')); }}
                      style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:12, padding:0, lineHeight:1, marginLeft:2 }}>
                      <i className="fa-solid fa-xmark" style={{ fontSize:10 }}></i>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Tag */}
            <div style={{ borderTop:'1px solid var(--border)', marginTop:16, paddingTop:16 }}>
              <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:8, textTransform:'uppercase' }}>Tag</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {[
                  { value: 'artist', color: '#3B82F6' },
                  { value: 'team', color: '#6366f1' },
                  { value: 'crew', color: '#22c55e' },
                  { value: 'family', color: '#f59e0b' },
                ].map(tag => (
                  <button key={tag.value} onClick={() => setBatchTag(tag.value)}
                    style={{ padding:'7px 16px', borderRadius:20, cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, textTransform:'capitalize',
                      background: batchTag === tag.value ? `${tag.color}15` : 'var(--bg)',
                      border: `1.5px solid ${batchTag === tag.value ? tag.color : 'var(--border)'}`,
                      color: batchTag === tag.value ? tag.color : 'var(--text3)' }}>
                    {tag.value}
                  </button>
                ))}
              </div>
            </div>

            {/* Access */}
            <div style={{ borderTop:'1px solid var(--border)', marginTop:16, paddingTop:16 }}>
              <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:8, textTransform:'uppercase' }}>Access</label>
              <div style={{ display:'flex', gap:8 }}>
                {['admin', 'viewer'].map(opt => (
                  <button key={opt} onClick={() => setBatchRole(opt)}
                    style={{ flex:1, padding:'10px 14px', borderRadius:10, cursor:'pointer', fontFamily:'inherit', textAlign:'center', textTransform:'capitalize',
                      background: batchRole === opt ? 'rgba(30,64,175,0.08)' : 'var(--bg)',
                      border: `2px solid ${batchRole === opt ? 'var(--blue)' : 'var(--border)'}`,
                      color: batchRole === opt ? 'var(--blue)' : 'var(--text3)', fontSize:13, fontWeight:700 }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {(() => {
            const emailCount = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@')).length;
            const ready = emailCount > 0 && batchArtistIds.length > 0;
            return (
              <button onClick={async () => {
                const emailList = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@'));
                if (!emailList.length || !batchArtistIds.length) return;
                setBatchSending(true); setBatchResults(null);
                try {
                  const primaryId = batchArtistIds[0];
                  const extraIds = batchArtistIds.slice(1);
                  const res = await apiFetch(`/artists/${primaryId}/invite-batch`, { method:'POST', body: JSON.stringify({ emails: emailList, artistIds: extraIds, role: batchRole, tag: batchTag }) });
                  setBatchResults(res.results || []);
                  setBatchEmails('');
                } catch (e) { setBatchResults([{ email: 'error', status: 'error', error: e.message }]); }
                finally { setBatchSending(false); }
              }} disabled={batchSending || !ready}
                style={{ width:'100%', padding:'16px', borderRadius:14, background: ready ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--bg)', border: ready ? 'none' : '1px solid var(--border)', color: ready ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor: ready ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', gap:10, boxShadow: ready ? '0 4px 16px rgba(30,64,175,0.3)' : 'none', marginBottom:20 }}>
                {batchSending ? 'Sending...' : <><i className="fa-solid fa-paper-plane" style={{ fontSize:14 }}></i> Send {emailCount || ''} {batchRole === 'admin' ? 'Admin' : 'Viewer'} Invite{emailCount !== 1 ? 's' : ''} for {batchArtistIds.length} Artist{batchArtistIds.length !== 1 ? 's' : ''}</>}
              </button>
            );
          })()}

          {batchResults && (
            <Card style={{ padding:16 }}>
              {batchResults.map((r, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < batchResults.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <i className={`fa-solid ${r.status === 'sent' ? 'fa-check-circle' : r.status === 'invalid' ? 'fa-exclamation-circle' : 'fa-times-circle'}`}
                    style={{ color: r.status === 'sent' ? '#22c55e' : r.status === 'invalid' ? '#f59e0b' : '#ef4444', fontSize:15 }}></i>
                  <span style={{ fontSize:14, color:'var(--text)', fontWeight:600 }}>{r.email}</span>
                  <span style={{ fontSize:12, color:'var(--text3)' }}>{r.status === 'sent' ? (r.isNewUser ? '- invite sent' : '- access granted') : r.status === 'invalid' ? '- invalid email' : `- ${r.error || 'failed'}`}</span>
                </div>
              ))}
            </Card>
          )}
        </div>

        {/* ═══ ACCESS CODES ═══ */}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:24, marginTop:8 }}>
          <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>
            <i className="fa-solid fa-key" style={{ marginRight:8, color:'#f59e0b' }}></i>Access Codes
          </h2>
          <p style={{ fontSize:14, color:'var(--text2)', marginBottom:16 }}>Share a code via text, group chat, or verbally</p>
          {artists.map(a => {
            const hasCode = !!a.accessCode;
            return (
              <Card key={a.id} style={{ padding:0, marginBottom:12, overflow: codeEmailArtist === a.id ? 'visible' : 'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px' }}>
                  <div style={{ width:44, height:44, borderRadius:'50%', overflow:'hidden', background:`${a.color||'var(--blue)'}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                    {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:0 }}>{a.name}</p>
                    {hasCode ? (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                        <span style={{ fontSize:18, fontWeight:900, color: a.color || 'var(--blue)', fontFamily:'monospace', letterSpacing:2 }}>{a.accessCode}</span>
                        <button onClick={() => navigator.clipboard.writeText(a.accessCode)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:13, padding:2 }}><i className="fa-solid fa-copy"></i></button>
                      </div>
                    ) : (
                      <p style={{ fontSize:12, color:'var(--text3)', margin:'2px 0 0' }}>No code generated</p>
                    )}
                  </div>
                  {hasCode ? (
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button onClick={async () => {
                        const text = `Join ${a.name}'s Day Sheets!\n\nSign up at https://daysheets.app\nAccess Code: ${a.accessCode}`;
                        if (navigator.share) {
                          try { await navigator.share({ title: `${a.name} Day Sheets Access`, text }); } catch {}
                        } else {
                          await navigator.clipboard.writeText(text);
                        }
                        setSharedArtistId(a.id);
                        setTimeout(() => setSharedArtistId(null), 2000);
                      }} style={{ padding:'10px 14px', borderRadius:10, background: sharedArtistId === a.id ? 'rgba(34,197,94,0.15)' : 'rgba(30,64,175,0.1)', border: sharedArtistId === a.id ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(30,64,175,0.25)', color: sharedArtistId === a.id ? '#22c55e' : 'var(--blue)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5, transition:'all 0.2s' }}>
                        <i className={`fa-solid ${sharedArtistId === a.id ? 'fa-check' : 'fa-share-nodes'}`} style={{ fontSize:11 }}></i> {sharedArtistId === a.id ? 'Copied!' : 'Share'}
                      </button>
                      <button onClick={() => { setCodeEmailArtist(codeEmailArtist === a.id ? null : a.id); setCodeEmailValue(''); setCodeEmailMsg(''); }}
                        style={{ padding:'10px 14px', borderRadius:10, background:'rgba(236,72,153,0.1)', border:'1px solid rgba(236,72,153,0.25)', color:'#ec4899', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                        <i className="fa-solid fa-envelope" style={{ fontSize:11 }}></i> Email
                      </button>
                    </div>
                  ) : (
                    <button onClick={async () => {
                      const prefix = a.name.replace(/[^a-zA-Z]/g,'').slice(0,6).toUpperCase();
                      const code = `${prefix}-${Math.floor(Math.random()*9000+1000)}`;
                      try { await apiFetch(`/artists/${a.id}`, { method:'PATCH', body:JSON.stringify({ accessCode:code }) }); setArtists(prev => prev.map(x => x.id===a.id ? {...x, accessCode:code} : x)); } catch(e) { alert(e.message); }
                    }} style={{ padding:'10px 16px', borderRadius:10, background:'linear-gradient(135deg,#f59e0b,#d97706)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                      <i className="fa-solid fa-key" style={{ fontSize:11 }}></i> Generate
                    </button>
                  )}
                </div>
                {codeEmailArtist === a.id && (
                  <div style={{ padding:'0 20px 16px', borderTop:'1px solid var(--border)' }}>
                    <div style={{ display:'flex', gap:8, paddingTop:12 }}>
                      <div style={{ flex:1 }}>
                        <EmailSearch value={codeEmailValue} onChange={setCodeEmailValue} onSelect={(email) => setCodeEmailValue(prev => (prev.trim() ? prev.trim()+', ' : '')+email)} placeholder="Search contacts or type email..." />
                      </div>
                      <button onClick={async () => {
                        const emailList = codeEmailValue.split(/[,\n]/).map(e=>e.trim()).filter(e=>e.includes('@'));
                        if (!emailList.length) return;
                        setCodeEmailSending(true); setCodeEmailMsg('');
                        try {
                          const res = await apiFetch(`/artists/${a.id}/send-code`, { method:'POST', body:JSON.stringify({ emails:emailList }) });
                          const sent = (res.results||[]).filter(r=>r.status==='sent').length;
                          setCodeEmailMsg(sent > 0 ? `Sent to ${sent} email${sent!==1?'s':''}!` : 'Failed to send');
                          if (sent > 0) setCodeEmailValue('');
                        } catch(e) { setCodeEmailMsg('Error: '+e.message); }
                        finally { setCodeEmailSending(false); }
                      }} disabled={codeEmailSending || !codeEmailValue.includes('@')}
                        style={{ padding:'10px 16px', borderRadius:10, background: codeEmailValue.includes('@') ? 'linear-gradient(135deg,#ec4899,#be185d)' : 'var(--bg)', border: codeEmailValue.includes('@') ? 'none' : '1px solid var(--border)', color: codeEmailValue.includes('@') ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: codeEmailValue.includes('@') ? 'pointer' : 'default', whiteSpace:'nowrap' }}>
                        {codeEmailSending ? '...' : 'Send'}
                      </button>
                    </div>
                    {codeEmailMsg && <p style={{ fontSize:12, color: codeEmailMsg.startsWith('Error') ? '#ef4444' : '#22c55e', marginTop:6, fontWeight:600 }}>{codeEmailMsg}</p>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Modals */}
        {modal && <ArtistModal artist={modal === 'new' ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />}
        {ConfirmDialog}
      </div>
    );
  }

  // Standalone invite page
  if (defaultInvite) {
    return (
      <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:640, margin:'0 auto' }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontSize:32, fontWeight:900, letterSpacing:-0.5, color:'var(--text)' }}>Invite to Artist</h1>
          <p style={{ fontSize:14, color:'var(--text2)', marginTop:4 }}>Send invite emails — single or batch</p>
        </div>

        {/* Artist selector — multi-select */}
        {artists.length > 0 && (
          <Card style={{ padding:20, marginBottom:20 }}>
            <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:10, textTransform:'uppercase' }}>Select Artists <span style={{ fontWeight:400, letterSpacing:0, textTransform:'none' }}>(tap multiple)</span></label>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
              {artists.map(a => {
                const sel = batchArtistIds.includes(a.id);
                const toggleArtist = () => setBatchArtistIds(prev => sel ? prev.filter(id => id !== a.id) : [...prev, a.id]);
                return (
                  <button key={a.id} onClick={toggleArtist}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700,
                      background: sel ? 'rgba(30,64,175,0.08)' : 'var(--bg)',
                      border: `2px solid ${sel ? 'var(--blue)' : 'var(--border)'}`,
                      color: sel ? 'var(--text)' : 'var(--text3)' }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', overflow:'hidden', background:`${a.color || 'var(--blue)'}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                    </div>
                    {a.name}
                    {sel && <i className="fa-solid fa-check" style={{ fontSize:12, color:'var(--blue)', marginLeft:2 }}></i>}
                  </button>
                );
              })}
            </div>
            {/* Show access codes for selected artists */}
            {batchArtistIds.length > 0 && artists.filter(a => batchArtistIds.includes(a.id) && a.accessCode).length > 0 && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:12 }}>
                {artists.filter(a => batchArtistIds.includes(a.id) && a.accessCode).map(a => (
                  <div key={a.id} style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(30,64,175,0.06)', border:'1px solid rgba(30,64,175,0.15)', borderRadius:10, padding:'5px 10px' }}>
                    <i className="fa-solid fa-key" style={{ fontSize:9, color:a.color || 'var(--blue)' }}></i>
                    <span style={{ fontSize:12, fontWeight:700, color:'var(--text)', fontFamily:'monospace', letterSpacing:1 }}>{a.accessCode}</span>
                    <span style={{ fontSize:10, color:'var(--text3)' }}>{a.name?.split(' ')[0]}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Email + Tag + Access — single card */}
        <Card style={{ padding:20, marginBottom:20 }}>
          <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:8, textTransform:'uppercase' }}>Email</label>
          <EmailSearch onSelect={(email) => {
            if (!email) return;
            const existing = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
            if (!existing.includes(email)) {
              setBatchEmails(prev => (prev.trim() ? prev.trim() + '\n' : '') + email);
            }
          }} placeholder="Search contacts or type email..." />

          {batchEmails.trim() && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:12 }}>
              {batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean).map((email, i) => (
                <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'5px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                  background: email.includes('@') ? 'rgba(30,64,175,0.1)' : 'rgba(239,68,68,0.1)',
                  border: `1px solid ${email.includes('@') ? 'rgba(30,64,175,0.25)' : 'rgba(239,68,68,0.25)'}`,
                  color: email.includes('@') ? 'var(--blue)' : '#ef4444' }}>
                  {email}
                  <button onClick={() => {
                    const list = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
                    list.splice(i, 1);
                    setBatchEmails(list.join('\n'));
                  }} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:12, padding:0, lineHeight:1, marginLeft:2 }}>
                    <i className="fa-solid fa-xmark" style={{ fontSize:10 }}></i>
                  </button>
                </span>
              ))}
            </div>
          )}

          <details style={{ marginTop:12 }}>
            <summary style={{ fontSize:11, color:'var(--text3)', cursor:'pointer', fontWeight:600 }}>
              <i className="fa-solid fa-paste" style={{ marginRight:4, fontSize:10 }}></i>
              Paste multiple emails
            </summary>
            <textarea value={batchEmails} onChange={e => setBatchEmails(e.target.value)}
              placeholder={"one@example.com\ntwo@example.com\nthree@example.com"}
              rows={4}
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit', resize:'vertical', marginTop:8 }} />
          </details>

          {/* Tag */}
          <div style={{ borderTop:'1px solid var(--border)', marginTop:16, paddingTop:16 }}>
            <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:8, textTransform:'uppercase' }}>Tag</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {[
                { value: 'artist', color: '#3B82F6' },
                { value: 'team', color: '#6366f1' },
                { value: 'crew', color: '#22c55e' },
                { value: 'family', color: '#f59e0b' },
              ].map(tag => (
                <button key={tag.value} onClick={() => setBatchTag(tag.value)}
                  style={{ padding:'8px 18px', borderRadius:20, cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, textTransform:'capitalize',
                    background: batchTag === tag.value ? `${tag.color}15` : 'var(--bg)',
                    border: `1.5px solid ${batchTag === tag.value ? tag.color : 'var(--border)'}`,
                    color: batchTag === tag.value ? tag.color : 'var(--text3)' }}>
                  {tag.value}
                </button>
              ))}
            </div>
          </div>

          {/* Access */}
          <div style={{ borderTop:'1px solid var(--border)', marginTop:16, paddingTop:16 }}>
            <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:8, textTransform:'uppercase' }}>Access</label>
            <div style={{ display:'flex', gap:8 }}>
              {['admin', 'viewer'].map(opt => (
                <button key={opt} onClick={() => setBatchRole(opt)}
                  style={{ flex:1, padding:'10px 16px', borderRadius:12, cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:700, textAlign:'center', textTransform:'capitalize',
                    background: batchRole === opt ? 'rgba(30,64,175,0.08)' : 'var(--bg)',
                    border: `2px solid ${batchRole === opt ? 'var(--blue)' : 'var(--border)'}`,
                    color: batchRole === opt ? 'var(--blue)' : 'var(--text3)' }}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Send */}
        {(() => {
          const emailCount = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@')).length;
          const ready = emailCount > 0 && batchArtistIds.length > 0;
          return (
            <button onClick={async () => {
              const emailList = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@'));
              if (!emailList.length || !batchArtistIds.length) return;
              setBatchSending(true); setBatchResults(null);
              try {
                const primaryId = batchArtistIds[0];
                const extraIds = batchArtistIds.slice(1);
                const res = await apiFetch(`/artists/${primaryId}/invite-batch`, {
                  method: 'POST', body: JSON.stringify({ emails: emailList, artistIds: extraIds, role: batchRole, tag: batchTag }),
                });
                setBatchResults(res.results || []);
                setBatchEmails('');
              } catch (e) { setBatchResults([{ email: 'error', status: 'error', error: e.message }]); }
              finally { setBatchSending(false); }
            }} disabled={batchSending || !ready}
              style={{ width:'100%', padding:'16px', borderRadius:14, background: ready ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--bg)', border: ready ? 'none' : '1px solid var(--border)', color: ready ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:16, fontWeight:800, cursor: ready ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', gap:10, boxShadow: ready ? '0 4px 16px rgba(30,64,175,0.3)' : 'none', marginBottom:20 }}>
              {batchSending ? 'Sending...' : <><i className="fa-solid fa-paper-plane" style={{ fontSize:14 }}></i> Send {emailCount || ''} {batchRole === 'admin' ? 'Admin' : 'Viewer'} Invite{emailCount !== 1 ? 's' : ''} for {batchArtistIds.length} Artist{batchArtistIds.length !== 1 ? 's' : ''}</>}
            </button>
          );
        })()}

        {/* Results */}
        {batchResults && (
          <Card style={{ padding:16, marginBottom:20 }}>
            {batchResults.map((r, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < batchResults.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <i className={`fa-solid ${r.status === 'sent' ? 'fa-check-circle' : r.status === 'invalid' ? 'fa-exclamation-circle' : 'fa-times-circle'}`}
                  style={{ color: r.status === 'sent' ? '#22c55e' : r.status === 'invalid' ? '#f59e0b' : '#ef4444', fontSize:15 }}></i>
                <span style={{ fontSize:14, color:'var(--text)', fontWeight:600 }}>{r.email}</span>
                <span style={{ fontSize:12, color:'var(--text3)' }}>
                  {r.status === 'sent' ? (r.isNewUser ? '— invite sent' : '— access granted') : r.status === 'invalid' ? '— invalid email' : `— ${r.error || 'failed'}`}
                </span>
              </div>
            ))}
          </Card>
        )}

        {/* ═══ ACCESS CODES SECTION ═══ */}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:28, marginTop:8 }}>
          <div style={{ marginBottom:20 }}>
            <h2 style={{ fontSize:22, fontWeight:900, color:'var(--text)', marginBottom:4 }}>
              <i className="fa-solid fa-key" style={{ marginRight:8, color:'#f59e0b' }}></i>
              Access Codes
            </h2>
            <p style={{ fontSize:14, color:'var(--text2)' }}>Share a code via text, group chat, or verbally — no email needed</p>
          </div>

          {artists.map(a => {
            const hasCode = !!a.accessCode;
            return (
              <Card key={a.id} style={{ padding:0, marginBottom:12, overflow: codeEmailArtist === a.id ? 'visible' : 'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px' }}>
                  <div style={{ width:44, height:44, borderRadius:'50%', overflow:'hidden', background:`${a.color || 'var(--blue)'}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                    {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:0 }}>{a.name}</p>
                    {hasCode ? (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                        <span style={{ fontSize:18, fontWeight:900, color: a.color || 'var(--blue)', fontFamily:'monospace', letterSpacing:2 }}>{a.accessCode}</span>
                        <button onClick={() => { navigator.clipboard.writeText(a.accessCode); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:13, padding:2 }}>
                          <i className="fa-solid fa-copy"></i>
                        </button>
                      </div>
                    ) : (
                      <p style={{ fontSize:12, color:'var(--text3)', margin:'2px 0 0' }}>No code generated</p>
                    )}
                  </div>
                  {hasCode ? (
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button onClick={async () => {
                        const text = `Join ${a.name}'s Day Sheets!\n\nSign up at https://daysheets.app\nAccess Code: ${a.accessCode}`;
                        if (navigator.share) {
                          try { await navigator.share({ title: `${a.name} Day Sheets Access`, text }); } catch {}
                        } else {
                          await navigator.clipboard.writeText(text);
                        }
                        setSharedArtistId(a.id);
                        setTimeout(() => setSharedArtistId(null), 2000);
                      }}
                        style={{ padding:'10px 14px', borderRadius:10, background: sharedArtistId === a.id ? 'rgba(34,197,94,0.15)' : 'rgba(30,64,175,0.1)', border: sharedArtistId === a.id ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(30,64,175,0.25)', color: sharedArtistId === a.id ? '#22c55e' : 'var(--blue)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5, transition:'all 0.2s' }}>
                        <i className={`fa-solid ${sharedArtistId === a.id ? 'fa-check' : 'fa-share-nodes'}`} style={{ fontSize:11 }}></i> {sharedArtistId === a.id ? 'Copied!' : 'Share'}
                      </button>
                      <button onClick={() => { setCodeEmailArtist(codeEmailArtist === a.id ? null : a.id); setCodeEmailValue(''); setCodeEmailMsg(''); }}
                        style={{ padding:'10px 14px', borderRadius:10, background:'rgba(236,72,153,0.1)', border:'1px solid rgba(236,72,153,0.25)', color:'#ec4899', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                        <i className="fa-solid fa-envelope" style={{ fontSize:11 }}></i> Email
                      </button>
                    </div>
                  ) : (
                    <button onClick={async () => {
                      const prefix = a.name.replace(/[^a-zA-Z]/g, '').slice(0, 6).toUpperCase();
                      const suffix = String(Math.floor(Math.random() * 9000) + 1000);
                      const code = `${prefix}-${suffix}`;
                      try {
                        await apiFetch(`/artists/${a.id}`, { method: 'PATCH', body: JSON.stringify({ accessCode: code }) });
                        setArtists(prev => prev.map(x => x.id === a.id ? { ...x, accessCode: code } : x));
                      } catch (e) { alert(e.message); }
                    }}
                      style={{ padding:'10px 16px', borderRadius:10, background:'linear-gradient(135deg,#f59e0b,#d97706)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                      <i className="fa-solid fa-key" style={{ fontSize:11 }}></i> Generate
                    </button>
                  )}
                </div>
                {/* Email code input */}
                {codeEmailArtist === a.id && (
                  <div style={{ padding:'0 20px 16px', borderTop:'1px solid var(--border)' }}>
                    <div style={{ display:'flex', gap:8, paddingTop:12 }}>
                      <div style={{ flex:1 }}>
                        <EmailSearch value={codeEmailValue} onChange={setCodeEmailValue} onSelect={(email) => {
                          setCodeEmailValue(prev => (prev.trim() ? prev.trim() + ', ' : '') + email);
                        }} placeholder="Search contacts or type email..." />
                      </div>
                      <button id={`send-code-${a.id}`} onClick={async () => {
                        const emailList = codeEmailValue.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@'));
                        if (!emailList.length) return;
                        setCodeEmailSending(true); setCodeEmailMsg('');
                        try {
                          const res = await apiFetch(`/artists/${a.id}/send-code`, { method: 'POST', body: JSON.stringify({ emails: emailList }) });
                          const sent = (res.results || []).filter(r => r.status === 'sent').length;
                          const errors = (res.results || []).filter(r => r.status === 'error');
                          if (sent > 0) {
                            setCodeEmailMsg(`Sent to ${sent} email${sent !== 1 ? 's' : ''}!`);
                            setCodeEmailValue('');
                          } else if (errors.length) {
                            setCodeEmailMsg('Error: ' + (errors[0].error || 'Failed to send'));
                          } else {
                            setCodeEmailMsg('No valid emails found');
                          }
                        } catch (e) { setCodeEmailMsg('Error: ' + e.message); }
                        finally { setCodeEmailSending(false); }
                      }} disabled={codeEmailSending || !codeEmailValue.includes('@')}
                        style={{ padding:'10px 16px', borderRadius:10, background: codeEmailValue.includes('@') ? 'linear-gradient(135deg,#ec4899,#be185d)' : 'var(--bg)', border: codeEmailValue.includes('@') ? 'none' : '1px solid var(--border)', color: codeEmailValue.includes('@') ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: codeEmailValue.includes('@') ? 'pointer' : 'default', whiteSpace:'nowrap' }}>
                        {codeEmailSending ? '...' : 'Send'}
                      </button>
                    </div>
                    {codeEmailMsg && <p style={{ fontSize:12, color: codeEmailMsg.startsWith('Error') ? '#ef4444' : '#22c55e', marginTop:6, fontWeight:600 }}>{codeEmailMsg}</p>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px 40px', maxWidth:900, margin:'0 auto' }}>
      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontSize:32, fontWeight:900, letterSpacing:-0.5, color:'var(--text)' }}>{usersOnly ? 'Users' : 'People'}</h1>
        <p style={{ fontSize:14, color:'var(--text2)', marginTop:4 }}>{usersOnly ? 'Team members and accounts' : 'Artists, team, and accounts'}</p>
      </div>
      {/* ═══ ARTISTS — MOBILE: circles / DESKTOP: cards ═══ */}
      {!usersOnly && (isMobile ? (
        /* ── Mobile: horizontal scrolling artist circles ── */
        <div style={{ marginBottom:20 }}>
          <div style={{ display:'flex', gap:16, overflowX:'auto', padding:'4px 0 12px', WebkitOverflowScrolling:'touch' }}>
            {artists.map(a => (
              <button key={a.id} onClick={() => setModal(a)}
                style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0, minWidth:68 }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:`${a.color}20`, border:`2.5px solid ${a.color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, overflow:'hidden' }}>
                  {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                </div>
                <span style={{ fontSize:11, fontWeight:700, color:'var(--text)', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name?.split(' ')[0]}</span>
              </button>
            ))}
            {/* Add new artist circle */}
            <button onClick={() => setModal('new')}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, flexShrink:0, minWidth:68 }}>
              <div style={{ width:60, height:60, borderRadius:'50%', background:'var(--bg)', border:'2px dashed var(--border)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="fa-solid fa-plus" style={{ fontSize:18, color:'var(--text3)' }}></i>
              </div>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--text3)' }}>Add</span>
            </button>
          </div>
          {/* Add Person button */}
          <button onClick={() => setPersonModal('add')}
            style={{ width:'100%', padding:'12px 16px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.25)' }}>
            <i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> Add Person
          </button>
        </div>
      ) : (
        /* ── Desktop: full artist cards ── */
        <>
          {artists.length === 0 ? (
            <Card style={{ padding:80, textAlign:'center' }}>
              <i className="fa-solid fa-music" style={{ fontSize:48, color:'var(--text3)', display:'block', marginBottom:16 }}></i>
              <p style={{ fontSize:18, fontWeight:700, color:'var(--text)', marginBottom:6 }}>No artists yet</p>
              <p style={{ fontSize:14, color:'var(--text2)', marginBottom:24 }}>Add your first artist to get started</p>
              <Btn variant="primary" size="lg" onClick={() => setModal('new')}>+ New Artist</Btn>
            </Card>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:20 }}>
              {artists.map(a => (
                <div key={a.id} style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden', transition:'transform 0.15s, box-shadow 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}>
                  <div style={{ height:56, background:`linear-gradient(180deg, ${a.color}15 0%, transparent 100%)` }} />
                  <div style={{ display:'flex', justifyContent:'center', marginTop:-32 }}>
                    <div style={{ width:64, height:64, borderRadius:16, background:`${a.color}15`, border:'3px solid var(--card)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:30, overflow:'hidden' }}>
                      {a.photo ? <img src={a.photo} alt={a.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                    </div>
                  </div>
                  <div style={{ textAlign:'center', padding:'10px 20px 0' }}>
                    <h2 style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:2 }}>{a.name}</h2>
                    {a.city && <p style={{ fontSize:13, color:'var(--text2)', marginBottom:0 }}><i className="fa-solid fa-location-dot" style={{ marginRight:4, fontSize:11 }}></i>{a.city}</p>}
                    {a.bio && <p style={{ fontSize:12, color:'var(--text3)', marginTop:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.bio}</p>}
                  </div>
                  <div style={{ display:'flex', justifyContent:'center', gap:6, flexWrap:'wrap', padding:'12px 16px 0' }}>
                    <span style={{ fontSize:11, color:'var(--text3)', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:20, padding:'3px 10px', fontWeight:600 }}>
                      <i className="fa-regular fa-clock" style={{ marginRight:4, fontSize:10 }}></i>{a.timezone?.replace('America/','').replace('_',' ')}
                    </span>
                    {a.googleCalendarId && (
                      <span style={{ fontSize:11, color:'#3fb950', background:'rgba(63,185,80,0.1)', border:'1px solid rgba(63,185,80,0.25)', borderRadius:20, padding:'3px 10px', fontWeight:600 }}>
                        <i className="fa-brands fa-google" style={{ marginRight:4, fontSize:9 }}></i>Calendar
                      </span>
                    )}
                    {a.distributionList?.length > 0 && (
                      <span style={{ fontSize:11, color:'var(--blue)', background:'rgba(79,156,249,0.1)', border:'1px solid rgba(79,156,249,0.25)', borderRadius:20, padding:'3px 10px', fontWeight:600 }}>
                        <i className="fa-solid fa-envelope" style={{ marginRight:4, fontSize:9 }}></i>{a.distributionList.length} recipient{a.distributionList.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <AccessCodeSection artist={a} onUpdate={(updated) => setArtists(prev => prev.map(x => x.id === updated.id ? updated : x))} />
                  <div style={{ display:'flex', gap:6, padding:'12px 16px 18px', justifyContent:'center' }}>
                    <Btn variant="ghost" size="sm" onClick={() => setInviting(a)}><i className="fa-solid fa-envelope" style={{ fontSize:11 }}></i></Btn>
                    <Btn variant="ghost" size="sm" onClick={() => setModal(a)}><i className="fa-solid fa-pen" style={{ fontSize:11 }}></i></Btn>
                    <Btn variant="danger" size="sm" onClick={() => handleDelete(a)} disabled={deleting === a.id}>{deleting === a.id ? '...' : <i className="fa-solid fa-trash" style={{ fontSize:11 }}></i>}</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ borderTop:'1px solid var(--border)', margin:'8px 0 20px' }} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
            <button onClick={() => setModal('new')}
              style={{ padding:'12px 16px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'1.5px solid transparent', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.25)' }}>
              <i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> New Artist
            </button>
            <button onClick={() => setPersonModal('add')}
              style={{ padding:'12px 16px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'1.5px solid transparent', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.25)' }}>
              <i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> Add Person
            </button>
          </div>
        </>
      )
      )}

      {/* ═══ INVITE TO ARTIST — moved to sidebar tab ═══ */}
      {false && (
      <Card style={{ padding:0, overflow:'hidden', marginBottom:20 }}>
        <button onClick={() => { setBatchOpen(!batchOpen); if (batchArtistIds.length === 0 && artists.length) setBatchArtistIds([artists[0].id]); }}
          style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 20px', width:'100%', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'rgba(30,64,175,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <i className="fa-solid fa-envelope" style={{ fontSize:14, color:'var(--blue)' }}></i>
          </div>
          <div style={{ flex:1 }}>
            <span style={{ fontSize:15, fontWeight:700, color:'var(--text)' }}>Invite to Artist</span>
            <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0' }}>Send invites by email — single or batch</p>
          </div>
          <i className={`fa-solid fa-chevron-${batchOpen ? 'up' : 'down'}`} style={{ fontSize:11, color:'var(--text3)' }}></i>
        </button>
        {batchOpen && (
          <div style={{ padding:'0 20px 20px', borderTop:'1px solid var(--border)' }}>
            {/* Artist selector — multi */}
            {artists.length > 1 && (
              <div style={{ paddingTop:14, marginBottom:12 }}>
                <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:6, textTransform:'uppercase' }}>Artists <span style={{ fontWeight:400, letterSpacing:0, textTransform:'none' }}>(select multiple)</span></label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {artists.map(a => {
                    const sel = batchArtistIds.includes(a.id);
                    return (
                      <button key={a.id} onClick={() => setBatchArtistIds(prev => sel ? prev.filter(id => id !== a.id) : [...prev, a.id])}
                        style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:10, cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
                          background: sel ? 'rgba(30,64,175,0.12)' : 'var(--bg)',
                          border: `1.5px solid ${sel ? (a.color || 'var(--blue)') : 'var(--border)'}`,
                          color: sel ? 'var(--text)' : 'var(--text3)' }}>
                        <div style={{ width:24, height:24, borderRadius:'50%', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, background:`${a.color||'var(--blue)'}20` }}>
                          {a.photo ? <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : (a.emoji || '🎵')}
                        </div>
                        {a.name?.split(' ')[0]}
                        {sel && <i className="fa-solid fa-check" style={{ fontSize:9, color:'var(--blue)' }}></i>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Email input */}
            <div style={{ paddingTop: artists.length <= 1 ? 14 : 0, marginBottom:12 }}>
              <label style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', display:'block', marginBottom:6, textTransform:'uppercase' }}>Email addresses</label>
              <textarea value={batchEmails} onChange={e => setBatchEmails(e.target.value)}
                placeholder={"one@example.com\ntwo@example.com\nthree@example.com\n\nor paste comma-separated"}
                rows={4}
                style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit', resize:'vertical' }} />
              <p style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                One per line or comma-separated — {batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@')).length} valid
              </p>
            </div>
            {/* Send button */}
            {(() => {
              const emailCount = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@')).length;
              const ready = emailCount > 0 && batchArtistIds.length > 0;
              return (
                <button onClick={async () => {
                  const emailList = batchEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes('@'));
                  if (!emailList.length || !batchArtistIds.length) return;
                  setBatchSending(true); setBatchResults(null);
                  try {
                    const primaryId = batchArtistIds[0];
                    const extraIds = batchArtistIds.slice(1);
                    const res = await apiFetch(`/artists/${primaryId}/invite-batch`, {
                      method: 'POST', body: JSON.stringify({ emails: emailList, artistIds: extraIds }),
                    });
                    setBatchResults(res.results || []);
                    setBatchEmails('');
                    apiFetch('/users').then(u => setUsers(Array.isArray(u) ? u : u.users || [])).catch(() => {});
                  } catch (e) { setBatchResults([{ email: 'error', status: 'error', error: e.message }]); }
                  finally { setBatchSending(false); }
                }} disabled={batchSending || !ready}
                  style={{ width:'100%', padding:'13px', borderRadius:12, background: ready ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--bg)', border: ready ? 'none' : '1px solid var(--border)', color: ready ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor: ready ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {batchSending ? 'Sending...' : <><i className="fa-solid fa-paper-plane" style={{ fontSize:12 }}></i> Send {emailCount || ''} Invite{emailCount !== 1 ? 's' : ''}</>}
                </button>
              );
            })()}
            {/* Results */}
            {batchResults && (
              <div style={{ marginTop:12 }}>
                {batchResults.map((r, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0', fontSize:12 }}>
                    <i className={`fa-solid ${r.status === 'sent' ? 'fa-check-circle' : r.status === 'invalid' ? 'fa-exclamation-circle' : 'fa-times-circle'}`}
                      style={{ color: r.status === 'sent' ? '#22c55e' : r.status === 'invalid' ? '#f59e0b' : '#ef4444', fontSize:13 }}></i>
                    <span style={{ color:'var(--text2)' }}>{r.email}</span>
                    <span style={{ color:'var(--text3)', fontSize:11 }}>
                      {r.status === 'sent' ? (r.isNewUser ? '— invite sent' : '— access granted') : r.status === 'invalid' ? '— invalid email' : `— ${r.error || 'failed'}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>)}

      {/* People list */}
      <Card style={{ padding:0, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>
            <i className="fa-solid fa-users" style={{ marginRight:5, fontSize:10 }}></i>ALL PEOPLE
          </p>
          <span style={{ fontSize:11, color:'var(--text3)', fontWeight:600 }}>{people.length}</span>
        </div>
        {people.length === 0 ? (
          <div style={{ padding:32, textAlign:'center', color:'var(--text3)' }}>
            <i className="fa-solid fa-users" style={{ fontSize:32, display:'block', marginBottom:8 }}></i>
            <p style={{ fontSize:14 }}>No team members yet</p>
          </div>
        ) : (
          people.map((p, i) => {
            const cap = (s) => s ? s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';
            const displayName = cap(p.fullName || p.name);
            const shortName = p.fullName && p.name !== p.fullName ? cap(p.name) : null;
            // Only show meaningful tags (not "other", "viewer" which are access roles not team tags)
            const displayTags = (p.tags || []).filter(t => t && !['other','viewer','admin','manager'].includes(t.toLowerCase()));
            return (
            <div key={p.id || p.email || i} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 20px', borderBottom: i < people.length-1 ? '1px solid rgba(51,65,85,0.5)' : 'none' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background: p.color || '#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:'#fff', flexShrink:0, textShadow:'0 1px 2px rgba(0,0,0,0.3)' }}>
                {(() => { const n = (p.name||'').toLowerCase().trim(); return ['kevin jonas','kevin jonas ii','paul kevin jonas ii','paul kevin jonas','paul jonas'].includes(n) || n.includes('kevin') && n.includes('jonas') ? 'K2' : ((p.name||'??').split(' ').filter(Boolean).map(w=>w[0]).join('') || '??').toUpperCase().slice(0,2); })()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>
                  {displayName}
                  {shortName && <span style={{ fontSize:11, color:'var(--text3)', marginLeft:6 }}>({shortName})</span>}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:2, flexWrap:'wrap' }}>
                  {displayTags.map(t => (
                    <span key={t} style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:0.5, color: ROLE_COLORS[t] || '#94a3b8' }}>{t}</span>
                  ))}
                  {p.email && <span style={{ fontSize:11, color:'var(--text3)' }}>{displayTags.length ? '· ' : ''}{p.email}</span>}
                </div>
                {(p.linkedArtists || []).length > 0 && (
                  <div style={{ display:'flex', gap:4, marginTop:4 }}>
                    {p.linkedArtists.map(a => (
                      <span key={a} style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:6, background:'var(--bg)', border:'1px solid var(--border)', color:'var(--text3)' }}>{a?.split(' ')[0]}</span>
                    ))}
                  </div>
                )}
              </div>
              {p.access && p.access !== 'none' && (
                <div style={{ display:'flex', flexDirection:'column', gap:3, alignItems:'flex-end', flexShrink:0 }}>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:8, textTransform:'uppercase',
                    color: p.access==='admin' ? '#1E40AF' : '#94a3b8',
                    background: p.access==='admin' ? 'rgba(30,64,175,0.12)' : 'rgba(148,163,184,0.1)',
                    border: `1px solid ${p.access==='admin' ? 'rgba(30,64,175,0.2)' : 'rgba(148,163,184,0.2)'}` }}>
                    {p.access}
                  </span>
                  {p.accountStatus === 'pending' ? (
                    <span style={{ fontSize:9, fontWeight:700, color:'#fbbf24', display:'flex', alignItems:'center', gap:3 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:'#fbbf24', display:'inline-block' }}></span> Pending
                    </span>
                  ) : p.accountStatus === 'active' ? (
                    <span style={{ fontSize:9, fontWeight:700, color:'#22c55e', display:'flex', alignItems:'center', gap:3 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:'#22c55e', display:'inline-block' }}></span> Active
                    </span>
                  ) : null}
                </div>
              )}
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <button onClick={() => setPersonModal(p)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:14, padding:'4px 6px' }}><i className="fa-solid fa-pen"></i></button>
                <button onClick={() => handlePersonDelete(p)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', fontSize:14, padding:'4px 6px', opacity:0.6 }}>✕</button>
              </div>
            </div>
            );
          })
        )}
      </Card>

      {/* Person Modal */}
      {personModal && (
        <PersonModal
          person={personModal === 'add' ? null : personModal}
          artistId={artists[0]?.id}
          artists={artists}
          onSave={handlePersonSave}
          onClose={() => setPersonModal(null)}
        />
      )}

      {modal && (
        <ArtistModal artist={modal === 'new' ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />
      )}
      {inviting && (
        <InviteModal artist={inviting} onClose={() => setInviting(null)} />
      )}
      {ConfirmDialog}
    </div>
  );
}
